import { useState, useRef, useCallback, useEffect } from 'react'
import {
  ArrowLeft, ChevronRight, Settings, Play, CheckCircle2, Circle,
  Loader2, Clock, AlertTriangle, BarChart3, FileCode, Network,
  Layers, Database, Zap, Package, Cpu, Building2, TrendingUp,
  ChevronDown, ChevronUp, UploadCloud, Download, XCircle, RefreshCw,
  Home, Workflow, Plug, FileBarChart, HelpCircle, User, BarChart2,
} from 'lucide-react'
import { getAuthHeaders } from '../../services/nodeExecutors/authHelper.js'
import FileUploadDropzone from '../designer/FileUploadDropzone'
import { NODE_EXECUTORS } from '../../services/nodeExecutors'
import { WorkflowExecutor, ExecutionEvents, createExecutionContext } from '../../services/workflowExecutor'
import { getStageSummary } from './stageSummaryAdapters'
import { getAPIBaseUrl } from '../../environment'
import './StandardView.css'
import '../runs/RunsView.css'

// ─── Step → pipeline node type mapping ──────────────────────────────────────

const STEP_NODE_TYPES = {
  'upload':              'pipeline-upload',
  'code-analysis':       'pipeline-code-analysis',
  'ai-naming':           'pipeline-ai-naming',
  'code-refactor':       'pipeline-refactor',
  'dependency-mapper':   'pipeline-dependencies',
  'monolith-identifier': 'pipeline-monolith',
  'data-analysis':       'pipeline-data',
  'discovery':           'pipeline-discovery',
  'architecture':        'pipeline-architecture',
  'service-gen':         'pipeline-service-gen',
  'pre-flight':          'pipeline-pre-flight',
  'java-packages':       'pipeline-java-package',
}

// Step order for auto-advance after completion
const STEP_ORDER = [
  'upload',
  'code-analysis', 'ai-naming', 'code-refactor',
  'dependency-mapper', 'monolith-identifier', 'data-analysis',
  'discovery', 'architecture',
  'service-gen', 'pre-flight', 'java-packages',
]

// ─── Full-pipeline node definitions (drives WorkflowExecutor) ────────────────
// Each entry maps our step ID → WorkflowExecutor node shape.
// The upload step (pipeline-upload) is handled separately before the executor starts.

const PIPELINE_STEP_DEFS = [
  { stepId: 'upload',              nodeType: 'pipeline-upload'       },
  { stepId: 'code-analysis',       nodeType: 'pipeline-code-analysis' },
  { stepId: 'ai-naming',           nodeType: 'pipeline-ai-naming'     },
  { stepId: 'code-refactor',       nodeType: 'pipeline-refactor'      },
  { stepId: 'dependency-mapper',   nodeType: 'pipeline-dependencies'  },
  { stepId: 'monolith-identifier', nodeType: 'pipeline-monolith'      },
  { stepId: 'data-analysis',       nodeType: 'pipeline-data'          },
  { stepId: 'discovery',           nodeType: 'pipeline-discovery'     },
  { stepId: 'architecture',        nodeType: 'pipeline-architecture'  },
  { stepId: 'service-gen',         nodeType: 'pipeline-service-gen'   },
  { stepId: 'pre-flight',          nodeType: 'pipeline-pre-flight'    },
  { stepId: 'java-packages',       nodeType: 'pipeline-java-package'  },
]

/**
 * Build a synthetic WorkflowExecutor-compatible workflow object from staged files.
 * Nodes are linked in a simple linear chain (step 0 → 1 → 2 → …).
 */
function buildPipelineWorkflow(stagedFiles, preloadedIngest = null) {
  const nodes = PIPELINE_STEP_DEFS.map(({ stepId, nodeType }) => ({
    id: stepId,
    data: {
      nodeType,
      enabled: true,
      config: nodeType === 'pipeline-upload'
        ? preloadedIngest
          ? {
              source_hash:  preloadedIngest.sourceHash,
              ingest_job_id: preloadedIngest.ingestJobId,
              sourceLanguage: 'cobol', targetLanguage: 'java', generateTypeMappings: true,
            }
          : { stagedFiles, sourceLanguage: 'cobol', targetLanguage: 'java', generateTypeMappings: true }
        : {},
    },
  }))

  const edges = PIPELINE_STEP_DEFS.slice(0, -1).map((def, i) => ({
    id: `e-${i}`,
    source: def.stepId,
    target: PIPELINE_STEP_DEFS[i + 1].stepId,
  }))

  return { id: 'standard-pipeline', nodes, edges }
}

// ─── Pipeline phases ─────────────────────────────────────────────────────────

const PHASES = [
  {
    id: 'analyze',
    label: 'Analyze',
    steps: [
      { id: 'code-analysis',       label: 'Code Analysis',       icon: FileCode  },
      { id: 'ai-naming',           label: 'AI Naming',           icon: Zap       },
      { id: 'code-refactor',       label: 'Code Refactor',       icon: Cpu       },
      { id: 'dependency-mapper',   label: 'Dependency Mapper',   icon: Network   },
      { id: 'monolith-identifier', label: 'Monolith Identifier', icon: Layers    },
      { id: 'data-analysis',       label: 'Data Analysis',       icon: Database  },
    ],
  },
  {
    id: 'business',
    label: 'Business',
    steps: [
      { id: 'discovery',    label: 'Discovery & ROI', icon: TrendingUp },
      { id: 'architecture', label: 'Architecture',    icon: Building2  },
    ],
  },
  {
    id: 'build',
    label: 'Build',
    steps: [
      { id: 'service-gen',   label: 'Service Generation', icon: Package      },
      { id: 'pre-flight',    label: 'Pre-Flight',         icon: CheckCircle2 },
      { id: 'java-packages', label: 'Java Packages',      icon: BarChart3    },
    ],
  },
]

// ─── Static stage descriptions ───────────────────────────────────────────────

const STAGE_INFO = {
  'upload': {
    title: 'Source Upload',
    description: 'Upload your COBOL source archive. RecodeIT will extract and inventory all programs, copybooks, and configuration files before analysis begins.',
  },
  'code-analysis': {
    title: 'Code Analysis',
    description: 'Transform and understand your application through AI-powered code analysis, dependency mapping, and modernization readiness assessment.',
    prerequisite: 'Waiting for Source Upload',
  },
  'ai-naming': {
    title: 'AI Naming',
    description: 'AI-powered renaming of COBOL identifiers to meaningful Java class and method names.',
    prerequisite: 'Waiting for Code Analysis',
  },
  'code-refactor': {
    title: 'Code Refactor',
    description: 'Structural refactoring of the generated Java code — splitting classes by responsibility.',
    prerequisite: 'Waiting for AI Naming',
  },
  'dependency-mapper': {
    title: 'Dependency Mapper',
    description: 'Maps all COBOL program dependencies, copybooks and external calls into a structured graph.',
    prerequisite: 'Waiting for Code Refactor',
  },
  'monolith-identifier': {
    title: 'Monolith Identifier',
    description: 'Identifies monolithic coupling patterns and recommends microservice boundaries.',
    prerequisite: 'Waiting for Dependency Mapper',
  },
  'data-analysis': {
    title: 'Data Analysis',
    description: 'Analyzes data models, DB schemas and normalization opportunities in the COBOL program.',
    prerequisite: 'Waiting for Monolith Identifier',
  },
  'discovery': {
    title: 'Discovery & ROI',
    description: 'Business-level discovery: effort estimates, ROI projections and risk assessment.',
    prerequisite: 'Complete the Analyze phase first',
  },
  'architecture': {
    title: 'Architecture',
    description: 'Target architecture recommendations — microservices topology, API design and deployment model.',
    prerequisite: 'Waiting for Discovery & ROI',
  },
  'service-gen': {
    title: 'Service Generation',
    description: 'Generates Java microservices from the analyzed COBOL programs using the target profile.',
    prerequisite: 'Complete the Business phase first',
  },
  'pre-flight': {
    title: 'Pre-Flight',
    description: 'Compiles and validates the generated Java code, auto-fixing common COBOL→Java issues.',
    prerequisite: 'Waiting for Service Generation',
  },
  'java-packages': {
    title: 'Java Packages',
    description: 'Packages the validated Java project into a deployable Maven/Gradle build artifact.',
    prerequisite: 'Waiting for Pre-Flight',
  },
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024)       return bytes + ' B'
  if (bytes < 1048576)    return (bytes / 1024).toFixed(1) + ' KB'
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB'
  return (bytes / 1073741824).toFixed(2) + ' GB'
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StepDot({ status }) {
  if (status === 'complete')    return <CheckCircle2  size={14} className="step-dot complete" />
  if (status === 'in-progress') return <Loader2       size={14} className="step-dot active spin" />
  if (status === 'error')       return <AlertTriangle size={14} className="step-dot error" />
  if (status === 'locked')      return <Circle        size={14} className="step-dot locked" />
  return <Circle size={14} className="step-dot pending" />
}

function MetricBadge({ badge }) {
  const labels = {
    active: 'Active', scanned: 'Scanned', mapped: 'Mapped',
    moderate: 'Moderate', good: 'Good', pending: 'Pending',
    complete: 'Complete', error: 'Error',
  }
  return <span className={`metric-badge badge-${badge}`}>{labels[badge] || badge}</span>
}

function UploadSidebarIcon({ status }) {
  if (status === 'complete')    return <CheckCircle2  size={14} className="sv-upload-check" />
  if (status === 'in-progress') return <Loader2       size={14} className="spin" style={{ color: 'var(--accent-yellow)' }} />
  if (status === 'error')       return <AlertTriangle size={14} style={{ color: 'var(--accent-red)' }} />
  return <UploadCloud size={14} className="sv-upload-icon" />
}

// ─── Stage Summary Panel ─────────────────────────────────────────────────────
// Renders the stage-dashboard view for completed stages.
// Receives a { metrics, findings } object from getStageSummary() and lays it out.

function SummaryMetricCard({ label, value, sub, badge }) {
  return (
    <div className={`ss-metric-card${badge ? ` ss-metric-card--${badge}` : ''}`}>
      {badge && <MetricBadge badge={badge} />}
      <p className="ss-metric-value">{value}</p>
      <p className="ss-metric-label">{label}</p>
      {sub && <p className="ss-metric-sub">{sub}</p>}
    </div>
  )
}

function CompletionBanner({ title }) {
  return (
    <div className="ss-complete-banner">
      <CheckCircle2 size={16} className="ss-complete-banner-icon" />
      <div className="ss-complete-banner-text">
        <span className="ss-complete-banner-label">{title} — Complete</span>
        <span className="ss-complete-banner-sub">This stage finished successfully. Findings and results are shown below.</span>
      </div>
    </div>
  )
}

// ─── Pre-Flight Failure Panel ─────────────────────────────────────────────────
// Shown instead of the generic error card when Pre-Flight fails.
// Fetches compile_result.json to display inline errors, provides direct
// downloads for failure artifacts, and gives a clear "what to do next" guide.

function PreFlightFailurePanel({ result, errorMessage, onRetry, running }) {
  const apiUrl = getAPIBaseUrl()
  const jobId = result?.jobs?.pre_flight_job_id
  const errorCount = result?.errors ?? 0

  const [inlineErrors, setInlineErrors] = useState(null)  // null = not yet fetched
  const [errorsLoading, setErrorsLoading] = useState(false)

  useEffect(() => {
    if (!jobId) return
    let cancelled = false
    setErrorsLoading(true)
    fetch(`${apiUrl}/pre-flight/results/json/${jobId}/compile_result.json`, {
      headers: getAuthHeaders(),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled) return
        setInlineErrors(data?.errors_preview || [])
      })
      .catch(() => { if (!cancelled) setInlineErrors([]) })
      .finally(() => { if (!cancelled) setErrorsLoading(false) })
    return () => { cancelled = true }
  }, [jobId, apiUrl])

  const downloadJson = (filename) => {
    if (!jobId) return
    const url = `${apiUrl}/pre-flight/results/json/${jobId}/${filename}`
    fetch(url, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.blob() : Promise.reject()))
      .then(blob => {
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(a.href)
      })
      .catch(() => window.open(url, '_blank'))
  }

  return (
    <div className="pf-failure-panel">

      {/* ── Failure summary ── */}
      <div className="pf-failure-header">
        <XCircle size={16} className="pf-failure-icon" />
        <div className="pf-failure-header-text">
          <p className="pf-failure-title">
            Compile check failed — {errorCount} error{errorCount !== 1 ? 's' : ''}
          </p>
          {errorMessage && (
            <p className="pf-failure-sub">{errorMessage}</p>
          )}
        </div>
      </div>

      {/* ── Inline error list ── */}
      {errorsLoading && (
        <div className="pf-errors-loading">
          <Loader2 size={12} className="spin" />
          Loading compile errors…
        </div>
      )}

      {!errorsLoading && inlineErrors && inlineErrors.length > 0 && (
        <div className="pf-errors-section">
          <p className="pf-section-heading">Top Compile Errors</p>
          <div className="pf-errors-list">
            {inlineErrors.slice(0, 8).map((e, i) => (
              <div key={i} className="pf-error-row">
                <span className="pf-error-loc">
                  {(e.file || '').split('/').pop() || e.file}
                  {e.line ? `:${e.line}` : ''}
                </span>
                <span className="pf-error-msg">{e.message}</span>
              </div>
            ))}
            {inlineErrors.length > 8 && (
              <p className="pf-errors-overflow">+{inlineErrors.length - 8} more — download the compile report for the full list</p>
            )}
          </div>
        </div>
      )}

      {!errorsLoading && inlineErrors && inlineErrors.length === 0 && (
        <p className="pf-errors-unavailable">
          Detailed error list unavailable — download the compile report to inspect the full Maven output.
        </p>
      )}

      {/* ── Action buttons ── */}
      <div className="pf-actions-section">
        <p className="pf-section-heading">Actions</p>
        <div className="pf-actions-row">
          {jobId && (
            <button
              className="pf-action-btn pf-action-btn--secondary"
              onClick={() => downloadJson('compile_result.json')}
              title="Download full Maven compile report (JSON)"
            >
              <Download size={13} />
              Download Compile Report
            </button>
          )}
          {jobId && (
            <button
              className="pf-action-btn pf-action-btn--secondary"
              onClick={() => downloadJson('compile_failure_snippets.json')}
              title="Download source snippets around each compile error"
            >
              <Download size={13} />
              Download Failure Snippets
            </button>
          )}
          {jobId && (
            <button
              className="pf-action-btn pf-action-btn--secondary"
              onClick={() => window.open(`${apiUrl}/artifacts/project-bundle/${jobId}`, '_blank')}
              title="Download the full generated Java project archive"
            >
              <Download size={13} />
              Download Generated Java
            </button>
          )}
          <button
            className="pf-action-btn pf-action-btn--retry"
            onClick={onRetry}
            disabled={running}
            title="Re-run Pre-Flight compile check"
          >
            <RefreshCw size={13} />
            Retry Pre-Flight
          </button>
        </div>
        <p className="pf-deferred-note">
          Download Failure Sources (individual .java files) — deferred, no zip endpoint yet.
        </p>
      </div>

      {/* ── Recommended next steps ── */}
      <div className="pf-nextsteps-section">
        <p className="pf-section-heading">Recommended Next Steps</p>
        <ol className="pf-nextsteps-list">
          <li>Review the compile errors above — most are missing imports or unresolved symbol references.</li>
          <li>Download Failure Snippets to see the exact source context around each error.</li>
          <li>If errors are in generated Java, return to Service Generation and adjust the target profile or apply manual edits before retrying.</li>
          <li>Click <strong>Retry Pre-Flight</strong> once corrections are in place.</li>
        </ol>
      </div>

    </div>
  )
}

function StageDownloadSection({ stepId, result }) {
  const apiUrl = getAPIBaseUrl()

  if (stepId === 'pre-flight') {
    const jobId = result?.jobs?.pre_flight_job_id
    if (!jobId) return null
    return (
      <div className="ss-downloads-section">
        <p className="ss-downloads-heading">Downloads</p>
        <div className="ss-downloads-grid">
          <div className="ss-download-card ss-download-card--primary">
            <div className="ss-download-card-info">
              <p className="ss-download-card-title">Deliverables Package</p>
              <p className="ss-download-card-desc">Generated Java files, analysis reports, and transformation artifacts from the Pre-Flight validation stage.</p>
            </div>
            <button
              className="ss-download-btn ss-download-btn--primary"
              onClick={() => window.open(`${apiUrl}/artifacts/project-bundle/${jobId}`, '_blank')}
            >
              <Download size={14} />
              Download Deliverables
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (stepId === 'java-packages') {
    const jobId = result?.jobs?.java_packaging_job_id
    if (!jobId) return null
    return (
      <div className="ss-downloads-section">
        <p className="ss-downloads-heading">Downloads</p>
        <div className="ss-downloads-grid">
          <div className="ss-download-card ss-download-card--primary">
            <div className="ss-download-card-info">
              <p className="ss-download-card-title">Generated Project</p>
              <p className="ss-download-card-desc">Ready-to-build Maven/Gradle Java project with all generated microservices and source files.</p>
            </div>
            <button
              className="ss-download-btn ss-download-btn--primary"
              onClick={() => window.open(`${apiUrl}/artifacts/project-bundle/${jobId}`, '_blank')}
            >
              <Download size={14} />
              Download Generated Project
            </button>
          </div>
          <div className="ss-download-card ss-download-card--secondary">
            <div className="ss-download-card-info">
              <p className="ss-download-card-title">Build Artifacts</p>
              <p className="ss-download-card-desc">Full artifact archive including compiled outputs, packaging logs, and build reports.</p>
            </div>
            <button
              className="ss-download-btn ss-download-btn--secondary"
              onClick={() => window.open(`${apiUrl}/artifacts/download/${jobId}`, '_blank')}
            >
              <Download size={14} />
              Download Build Artifacts
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}

function SummaryFindingCard({ title, body, rows, tags }) {
  return (
    <div className="ss-finding-card">
      <p className="ss-finding-title">{title}</p>
      {body && body.split('\n').map((line, i) => (
        line.trim() ? <p key={i} className="ss-finding-body">{line}</p> : null
      ))}
      {rows?.length > 0 && (
        <div className="ss-finding-rows">
          {rows.map((row, i) => (
            <div key={i} className={`ss-finding-row${row.bold ? ' ss-finding-row-bold' : ''}`}>
              <span className="ss-finding-row-label">{row.label}</span>
              {row.value != null && row.value !== '' && (
                <span className="ss-finding-row-value">{row.value}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {tags?.length > 0 && (
        <div className="ss-finding-tags">
          {tags.map((tag, i) => (
            <span key={i} className="ss-finding-tag">{tag}</span>
          ))}
        </div>
      )}
    </div>
  )
}

function StageSummaryPanel({ summary, title }) {
  if (!summary) return null
  const { metrics = [], findings = [] } = summary
  return (
    <div className="ss-panel">
      {title && <p className="ss-panel-title">{title}</p>}
      {metrics.length > 0 && (
        <div className="ss-metrics">
          {metrics.map((m, i) => <SummaryMetricCard key={i} {...m} />)}
        </div>
      )}
      {findings.length > 0 && (
        <div className="ss-findings-section">
          <p className="ss-findings-heading">Key Findings</p>
          <div className="ss-findings-grid">
            {findings.map((f, i) => <SummaryFindingCard key={i} {...f} />)}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────

function StandardView({ project, run, onBack, onBackToProjects, onSwitchToAdvanced, onOpenHelp, onOpenSettings, onOpenReports, userProfile = {} }) {
  const preloadedIngest = run?.preloadedIngest || null

  const [activeStep,      setActiveStep]      = useState('upload')
  const [expandedPhases,  setExpandedPhases]  = useState({ analyze: true, build: true, business: true })
  const [stagedFiles,     setStagedFiles]     = useState([])
  const [stepStatuses,    setStepStatuses]    = useState({
    'upload':              'pending',   // upload is always open
    'code-analysis':       'locked',
    'ai-naming':           'locked',
    'code-refactor':       'locked',
    'dependency-mapper':   'locked',
    'monolith-identifier': 'locked',
    'data-analysis':       'locked',
    'discovery':           'locked',
    'architecture':        'locked',
    'service-gen':         'locked',
    'pre-flight':          'locked',
    'java-packages':       'locked',
  })
  const [stepResults,    setStepResults]    = useState({})
  const [running,        setRunning]        = useState(false)
  const [runProgress,    setRunProgress]    = useState({ pct: 0, msg: '' })
  const [runError,       setRunError]       = useState(null)
  const [pulseDropzone,  setPulseDropzone]  = useState(false)   // highlights the dropzone when user clicks Run with no file

  // Persistent execution context — mutated in-place, not re-render-driven
  const execCtxRef    = useRef(null)
  // Running WorkflowExecutor instance — so we can abort on unmount / back
  const wfExecutorRef = useRef(null)

  const getExecCtx = useCallback(() => {
    if (!execCtxRef.current) {
      let userId = ''
      try {
        userId = JSON.parse(localStorage.getItem('modernizeit_user'))?.user_id || ''
      } catch (_) {}

      execCtxRef.current = createExecutionContext(
        { account_id: project?.account || '' },
        { app_name: project?.name || project?.application_name || project?.app_name || '' },
        getAPIBaseUrl(),
        userId,
      )
    }
    return execCtxRef.current
  }, [project])

  const togglePhase = (phaseId) =>
    setExpandedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }))

  // ── "Run Current Stage" / "Upload & Continue" handler ─────────────────────
  //
  // When called on the UPLOAD step:
  //   → builds a WorkflowExecutor for the entire full pipeline
  //   → events drive Journey state live as each node starts / completes
  //
  // When called on any other step (manual re-run after error or from sidebar):
  //   → runs just that one node's executor directly

  const handleRun = async () => {
    if (running) return

    // ── Upload step: start the full pipeline ─────────────────────────────────
    if (activeStep === 'upload') {
      if (stagedFiles.length === 0 && !preloadedIngest) {
        setPulseDropzone(true)
        setTimeout(() => setPulseDropzone(false), 1200)
        return
      }

      const ctx = getExecCtx()
      const workflow = buildPipelineWorkflow(stagedFiles, preloadedIngest)
      const executor = new WorkflowExecutor(workflow, ctx)
      wfExecutorRef.current = executor

      setRunning(true)
      setRunError(null)
      setRunProgress({ pct: 0, msg: 'Starting pipeline…' })

      // — NODE_START: mark step in-progress, make it the active step ——————
      executor.on(ExecutionEvents.NODE_START, ({ nodeId }) => {
        setStepStatuses(prev => ({ ...prev, [nodeId]: 'in-progress' }))
        setActiveStep(nodeId)
        setRunProgress({ pct: 0, msg: `Starting ${STAGE_INFO[nodeId]?.title || nodeId}…` })
      })

      // — NODE_PROGRESS: forward progress to the progress bar ————————————
      executor.on(ExecutionEvents.NODE_PROGRESS, ({ nodeId, progress, message }) => {
        setRunProgress({ pct: progress ?? 0, msg: message || '' })
      })

      // — NODE_COMPLETE: mark done, store result ——————————————————————————
      executor.on(ExecutionEvents.NODE_COMPLETE, ({ nodeId, result }) => {
        setStepStatuses(prev => ({ ...prev, [nodeId]: 'complete' }))
        if (result) setStepResults(prev => ({ ...prev, [nodeId]: result }))
        setRunProgress({ pct: 100, msg: `${STAGE_INFO[nodeId]?.title || nodeId} complete` })
      })

      // — NODE_ERROR: mark error, store partial result, show message ——————————
      // nodeResult carries the partial output (jobs.pre_flight_job_id, errors, etc.)
      // so the failure panel can show inline errors and wire downloads.
      executor.on(ExecutionEvents.NODE_ERROR, ({ nodeId, error, nodeResult }) => {
        setStepStatuses(prev => ({ ...prev, [nodeId]: 'error' }))
        if (nodeResult) setStepResults(prev => ({ ...prev, [nodeId]: nodeResult }))
        setRunError(typeof error === 'string' ? error : (error?.message || 'Stage failed'))
        setRunProgress({ pct: 0, msg: '' })
        setRunning(false)
        wfExecutorRef.current = null
      })

      // — WORKFLOW_COMPLETE ————————————————————————————————————————————————
      executor.on(ExecutionEvents.WORKFLOW_COMPLETE, () => {
        setRunning(false)
        setRunProgress({ pct: 100, msg: 'Pipeline complete!' })
        wfExecutorRef.current = null
      })

      // — WORKFLOW_ERROR ————————————————————————————————————————————————————
      // error is already a plain string from WorkflowExecutor
      executor.on(ExecutionEvents.WORKFLOW_ERROR, ({ error }) => {
        setRunning(false)
        setRunError(typeof error === 'string' ? error : (error?.message || 'Pipeline failed'))
        setRunProgress({ pct: 0, msg: '' })
        wfExecutorRef.current = null
      })

      // Fire it off — events will drive the UI from here.
      // We intentionally do NOT await — events drive all state updates above.
      executor.execute().catch(err => {
        // Only fires if the promise rejects AND no WORKFLOW_ERROR listener caught it
        console.error('[StandardView] WorkflowExecutor.execute() threw:', err)
        if (wfExecutorRef.current) {
          setRunning(false)
          setRunError(err.message || 'Pipeline failed unexpectedly')
          wfExecutorRef.current = null
        }
      })

      return
    }

    // ── Non-upload step: single-node manual re-run ───────────────────────────
    const nodeType = STEP_NODE_TYPES[activeStep]
    const nodeExecutor = NODE_EXECUTORS[nodeType]
    if (!nodeExecutor) {
      console.warn(`[StandardView] No executor for: ${activeStep} → ${nodeType}`)
      return
    }

    const ctx = getExecCtx()
    const config = { source_hash: ctx.source_hash, ...ctx.jobs }

    setRunning(true)
    setRunError(null)
    setRunProgress({ pct: 0, msg: 'Starting…' })
    setStepStatuses(prev => ({ ...prev, [activeStep]: 'in-progress' }))

    try {
      const result = await nodeExecutor.execute(
        config,
        ctx,
        (pct, msg) => setRunProgress({ pct, msg }),
        null,
      )

      if (result.jobs)        Object.assign(ctx.jobs, result.jobs)
      if (result.source_hash) ctx.source_hash = result.source_hash

      setStepResults(prev => ({ ...prev, [activeStep]: result }))
      setStepStatuses(prev => ({ ...prev, [activeStep]: 'complete' }))
      setRunProgress({ pct: 100, msg: 'Complete' })

      // Auto-advance to next step
      const idx = STEP_ORDER.indexOf(activeStep)
      if (idx >= 0 && idx < STEP_ORDER.length - 1) {
        setActiveStep(STEP_ORDER[idx + 1])
      }
    } catch (err) {
      console.error(`[StandardView] ${activeStep} failed:`, err)
      setStepStatuses(prev => ({ ...prev, [activeStep]: 'error' }))
      if (err.nodeResult) setStepResults(prev => ({ ...prev, [activeStep]: err.nodeResult }))
      setRunError(err.message || 'Stage failed')
      setRunProgress({ pct: 0, msg: '' })
    } finally {
      setRunning(false)
    }
  }

  // ── Auto-start pipeline when coming from guided upload ────────────────────
  // run.autoStart is set by App.jsx after GuidedUploadView completes upload.
  // Fires once, then the flag is consumed.
  const autoStartConsumed = useRef(false)
  useEffect(() => {
    if (run?.autoStart && preloadedIngest && !autoStartConsumed.current && !running) {
      autoStartConsumed.current = true
      console.log('[StandardView] Auto-starting pipeline from guided upload')
      // Small delay to let the component mount fully
      const timer = setTimeout(() => handleRun(), 300)
      return () => clearTimeout(timer)
    }
  }, [run?.autoStart, preloadedIngest, running]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ─────────────────────────────────────────────────────────

  const currentStatus = stepStatuses[activeStep]
  const currentResult = stepResults[activeStep]
  const info          = STAGE_INFO[activeStep] || { title: activeStep, description: '' }
  const runName       = run?.display_name || run?.workflow_name || run?.name || 'New Run'

  // ── Header user menu ──────────────────────────────────────────────────────
  const [showUserMenu, setShowUserMenu] = useState(false)
  const getShortName = () => {
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName.charAt(0)}.`
    }
    return userProfile.displayName || 'User'
  }

  const canRun = !running && currentStatus !== 'in-progress' && currentStatus !== 'locked'

  // Progress per phase (for right panel)
  const phaseProgress = PHASES.map(phase => {
    const total  = phase.steps.length
    const done   = phase.steps.filter(s => stepStatuses[s.id] === 'complete').length
    const active = phase.steps.some(s => stepStatuses[s.id] === 'in-progress')
    return { ...phase, total, done, active }
  })

  // ── Run button label ───────────────────────────────────────────────────────

  const runBtnLabel = () => {
    if (running) return runProgress.pct > 0 ? `${Math.round(runProgress.pct)}%` : 'Starting…'
    if (activeStep === 'upload') return preloadedIngest ? 'Start Re-Run' : 'Upload & Continue'
    if (currentStatus === 'complete') return `Re-run ${info.title}`
    return 'Run Current Stage'
  }

  // ── Main content renderer ──────────────────────────────────────────────────
  //
  // Upload step has its own UI (dropzone). All other stages render a stable
  // dashboard shell immediately — regardless of running/pending/complete status.
  // The dashboard fills in from real adapter data as it becomes available.

  const renderContent = () => {

    // ── Upload step ───────────────────────────────────────────────────────────
    if (activeStep === 'upload') {
      const r          = currentResult
      const staged     = stagedFiles[0]
      const isComplete = currentStatus === 'complete'
      const isRunning  = currentStatus === 'in-progress'

      if (isComplete) {
        const summary = getStageSummary('upload', r)
        if (summary) return <StageSummaryPanel summary={summary} />
      }

      // ── Re-run: source archive pre-loaded from previous run ───────────────
      if (preloadedIngest && !isRunning && !isComplete) {
        const { sourceHash, filesCount } = preloadedIngest
        return (
          <div className="sv-stage-shell">
            <div className="ss-metrics">
              <SummaryMetricCard
                label="Upload Status"
                value="Source Loaded"
                sub="Previous run's archive is ready. Click Start Re-Run to begin."
                badge="scanned"
              />
              <SummaryMetricCard
                label="Source Archive"
                value={sourceHash ? `${sourceHash.slice(0, 12)}…` : 'Reusing previous'}
                sub="Existing archive in S3 — no re-upload needed"
                badge="scanned"
              />
              <SummaryMetricCard
                label="Files Extracted"
                value={filesCount != null ? String(filesCount) : '—'}
                sub={filesCount != null ? 'From previous run' : 'Available after pipeline starts'}
                badge={filesCount != null ? 'scanned' : 'pending'}
              />
            </div>
            <div className="sv-insights-section">
              <div className="sv-upload-header-row">
                <div>
                  <h3 className="sv-insights-title">Source Archive Ready</h3>
                  <p className="sv-insights-sub">
                    The original source archive from the previous run will be reused — no re-upload required.
                    Click <strong>Start Re-Run</strong> to kick off a fresh pipeline execution.
                  </p>
                </div>
                <button
                  className="sv-upload-run-btn"
                  onClick={handleRun}
                  disabled={running}
                >
                  {running ? <Loader2 size={15} className="spin" /> : <Play size={15} />}
                  Start Re-Run
                </button>
              </div>
              {runError && (
                <div className="sv-error-notice">
                  <AlertTriangle size={15} />
                  <span>{runError}</span>
                </div>
              )}
            </div>
          </div>
        )
      }

      return (
        <div className="sv-stage-shell">
          {/* Integrated progress strip while uploading */}
          {isRunning && (
            <div className="sv-progress-strip">
              <div className="sv-progress-strip-track">
                <div className="sv-progress-strip-fill" style={{ width: `${Math.max(runProgress.pct || 0, 4)}%` }} />
              </div>
              <div className="sv-progress-strip-label">
                <Loader2 size={11} className="spin" />
                <span>{runProgress.msg || 'Uploading…'}</span>
                {runProgress.pct > 0 && <span className="sv-progress-strip-pct">{Math.round(runProgress.pct)}%</span>}
              </div>
            </div>
          )}

          {/* Metric row */}
          <div className="ss-metrics">
            <SummaryMetricCard
              label="Upload Status"
              value={isRunning ? 'Uploading' : staged ? 'Ready' : 'Waiting'}
              sub={isRunning ? (runProgress.msg || 'Processing archive…') : staged ? 'Click Upload & Continue to start the pipeline' : 'Select a .zip, .tar, or .gz archive below'}
              badge={isRunning ? 'active' : staged ? 'scanned' : 'pending'}
            />
            <SummaryMetricCard
              label="Source File"
              value={staged ? staged.name : '—'}
              sub={staged ? formatBytes(staged.size) : 'No file selected'}
              badge={staged ? 'scanned' : 'pending'}
            />
            <SummaryMetricCard
              label="Files Extracted"
              value="—"
              sub="Available after upload completes"
              badge="pending"
            />
          </div>

          {/* Dropzone — hidden while running */}
          {!isRunning && (
            <div className="sv-insights-section">
              <div className="sv-upload-header-row">
                <div>
                  <h3 className="sv-insights-title">Upload Your Source Code</h3>
                  <p className="sv-insights-sub">
                    Drag and drop your COBOL archive here, or click to browse.
                    Uploading will kick off the full analysis pipeline automatically.
                  </p>
                </div>
                <button
                  className={`sv-upload-run-btn ${stagedFiles.length === 0 ? 'sv-upload-run-btn-waiting' : ''}`}
                  onClick={handleRun}
                  disabled={running}
                  title={stagedFiles.length === 0 ? 'Select a file first' : 'Upload & start pipeline'}
                >
                  {running ? <Loader2 size={15} className="spin" /> : <Play size={15} />}
                  Upload &amp; Continue
                </button>
              </div>
              <div className={`sv-dropzone-wrapper ${pulseDropzone ? 'sv-dropzone-pulse' : ''}`}>
                <FileUploadDropzone
                  config={{ accept: '.zip,.tar,.gz' }}
                  onFileStaged={setStagedFiles}
                />
              </div>
              {runError && (
                <div className="sv-error-notice">
                  <AlertTriangle size={15} />
                  <span>{runError}</span>
                </div>
              )}
            </div>
          )}

          {/* Running: show what's happening instead of the dropzone */}
          {isRunning && (
            <div className="ss-findings-section">
              <p className="ss-findings-heading">Pipeline Starting</p>
              <div className="ss-findings-grid">
                <SummaryFindingCard
                  title="What Happens Next"
                  body="Your archive is being uploaded and extracted. Once complete, the full analysis pipeline will run automatically — Code Analysis, AI Naming, Refactor, and all remaining stages."
                />
                <SummaryFindingCard
                  title="Source File"
                  body={preloadedIngest
                    ? 'Reusing previously uploaded archive — skipping upload, starting pipeline directly.'
                    : staged ? `${staged.name} (${formatBytes(staged.size)}) — uploading to your secure S3 workspace.` : 'Archive is being processed.'}
                />
              </div>
            </div>
          )}
        </div>
      )
    }

    // ── All other stages — unified stable dashboard shell ─────────────────────
    //
    // This renders immediately in any state. The layout never changes —
    // only the content of the cards updates as data becomes available.

    const summary    = currentResult ? getStageSummary(activeStep, currentResult) : null
    const isRunning  = currentStatus === 'in-progress'
    const isComplete = currentStatus === 'complete'
    const isError    = currentStatus === 'error'
    const isLocked   = currentStatus === 'locked'

    // Status metric card — always shown as the first card
    const statusCard = {
      label: 'Stage Status',
      value: isRunning  ? 'Running'   : isComplete ? 'Complete'
           : isError    ? 'Failed'    : isLocked   ? 'Locked'   : 'Ready',
      sub:   isRunning  ? (runProgress.msg || 'Stage is processing…')
           : isComplete ? 'Stage completed successfully'
           : isError    ? 'Use Re-run in the topbar to retry'
           : isLocked   ? 'Will run automatically via the pipeline'
           : 'Click Run Current Stage in the topbar to begin',
      badge: isRunning  ? 'active'  : isComplete ? 'complete'
           : isError    ? 'moderate' : 'pending',
    }

    // Real metrics from adapter (slots 2–4)
    const adapterMetrics = summary?.metrics || []
    const displayMetrics = [statusCard, ...adapterMetrics].slice(0, 4)

    return (
      <div className="sv-stage-shell">

        {/* Integrated progress strip — replaces the old full-page spinner */}
        {isRunning && (
          <div className="sv-progress-strip">
            <div className="sv-progress-strip-track">
              <div className="sv-progress-strip-fill" style={{ width: `${Math.max(runProgress.pct || 0, 4)}%` }} />
            </div>
            <div className="sv-progress-strip-label">
              <Loader2 size={11} className="spin" />
              <span>{runProgress.msg || 'Processing…'}</span>
              {runProgress.pct > 0 && <span className="sv-progress-strip-pct">{Math.round(runProgress.pct)}%</span>}
            </div>
          </div>
        )}

        {/* Error notice — compact inline bar; suppressed for pre-flight (panel has its own header) */}
        {isError && activeStep !== 'pre-flight' && (
          <div className="sv-error-notice">
            <AlertTriangle size={15} />
            <span>{runError || 'Stage failed. Click Re-run in the topbar to retry.'}</span>
          </div>
        )}

        {/* Metric cards — stable grid, real data populates as available */}
        <div className="ss-metrics">
          {displayMetrics.map((m, i) => <SummaryMetricCard key={i} {...m} />)}
          {/* If adapter gave no metrics yet, show an "about" card as a placeholder */}
          {adapterMetrics.length === 0 && (
            <div className="ss-metric-card ss-metric-about">
              <p className="ss-metric-label">About This Stage</p>
              <p className="ss-metric-about-body">{info.description}</p>
            </div>
          )}
        </div>

        {/* Completion banner — green success strip shown above findings when done */}
        {isComplete && <CompletionBanner title={info.title} />}

        {/* Pre-Flight failure panel — replaces generic error findings for this node */}
        {isError && activeStep === 'pre-flight' && (
          <PreFlightFailurePanel
            result={currentResult}
            errorMessage={runError}
            onRetry={handleRun}
            running={running}
          />
        )}

        {/* Findings section — real data when complete, descriptive content otherwise */}
        {/* Skipped for failed pre-flight (handled above by PreFlightFailurePanel) */}
        {!(isError && activeStep === 'pre-flight') && (
          isComplete && summary?.findings?.length ? (
            <div className="ss-findings-section">
              <p className="ss-findings-heading">Key Findings</p>
              <div className="ss-findings-grid">
                {summary.findings.map((f, i) => <SummaryFindingCard key={i} {...f} />)}
              </div>
            </div>
          ) : (
            <div className="ss-findings-section">
              <p className="ss-findings-heading">
                {isRunning ? 'In Progress' : isError ? 'Stage Details' : 'About This Stage'}
              </p>
              <div className="ss-findings-grid">
                <SummaryFindingCard
                  title="What This Stage Does"
                  body={info.description}
                />
                {isRunning && (
                  <SummaryFindingCard
                    title="Current Activity"
                    body={runProgress.msg || `${info.title} is running. Results will appear here when the stage completes.`}
                  />
                )}
                {isError && (
                  <SummaryFindingCard
                    title="Error Details"
                    body={runError || 'An error occurred during stage execution. Review logs and click Re-run to retry.'}
                  />
                )}
                {(isLocked || currentStatus === 'pending') && info.prerequisite && (
                  <SummaryFindingCard
                    title="Waiting For"
                    body={isLocked
                      ? `${info.prerequisite}. This stage will start automatically once the pipeline reaches it.`
                      : info.prerequisite}
                  />
                )}
              </div>
            </div>
          )
        )}

        {/* Downloads — Pre-Flight and Java Packages expose artifact download buttons */}
        {isComplete && currentResult && (
          <StageDownloadSection stepId={activeStep} result={currentResult} />
        )}
      </div>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="standard-view">

      {/* ── Header — same shell as Transformation Runs (RunsView) ──────────── */}
      <div className="runs-header-bar">

        {/* Left: back + breadcrumb */}
        <div className="runs-header-left">
          <button className="runs-back-btn" onClick={onBack}>
            <ArrowLeft size={15} />
            <span>Runs</span>
          </button>
          <div className="runs-breadcrumb">
            <span className="runs-bc-project">{project?.name || 'Project'}</span>
            <ChevronRight size={14} className="runs-bc-sep" />
            <span className="runs-bc-page">{runName}</span>
          </div>
        </div>

        {/* Center: mode-switcher nav (Workflow active) */}
        <div className="runs-header-center">
          <div className="mode-switcher">
            <button className="mode-btn" onClick={onBack} title="Home — back to Runs">
              <Home size={16} />
              <span>Home</span>
            </button>
            <button className="mode-btn" onClick={onBackToProjects} title="Analyze — back to analysis">
              <BarChart2 size={16} />
              <span>Analyze</span>
            </button>
            <button className="mode-btn active" title="Workflow — guided modernization pipeline">
              <Workflow size={16} />
              <span>Workflow</span>
            </button>
            <button className="mode-btn" disabled title="MCP — coming soon">
              <Plug size={16} />
              <span>MCP</span>
            </button>
            <button
              className="mode-btn"
              onClick={() => onOpenReports?.()}
              title="Reports (opens in new tab)"
            >
              <FileBarChart size={16} />
              <span>Reports</span>
            </button>
          </div>
        </div>

        {/* Right: settings, help, user menu */}
        <div className="runs-header-right">
          <button className="icon-btn" onClick={onOpenSettings} title="Settings">
            <Settings size={18} />
          </button>
          <button className="icon-btn" onClick={onOpenHelp} title="Help">
            <HelpCircle size={18} />
          </button>
          <div className="user-menu-container">
            <button className="user-btn" onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className="user-avatar"><User size={16} /></div>
              <span>{getShortName()}</span>
              <ChevronDown size={14} />
            </button>
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <span className="user-name">{userProfile.displayName || 'User'}</span>
                  <span className="user-email">{userProfile.email || ''}</span>
                </div>
                <div className="dropdown-divider" />
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onOpenSettings?.() }}>
                  <Settings size={16} />
                  <span>Settings</span>
                </button>
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onBack() }}>
                  <Home size={16} />
                  <span>Back to Runs</span>
                </button>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Sub-header: workspace context + Run Current Stage button ── */}
      <div className="sv-subheader">
        <span className="sv-subheader-label">Guided modernization workspace</span>
        {activeStep !== 'upload' && (
          <button
            className={`sv-run-btn ${!canRun ? 'sv-run-btn-disabled' : ''} ${running ? 'sv-run-btn-running' : ''}`}
            onClick={handleRun}
            disabled={!canRun}
          >
            {running
              ? <Loader2 size={14} className="spin" />
              : <Play size={14} />}
            {runBtnLabel()}
          </button>
        )}
      </div>

      {/* ── Body: sidebar + main + progress ── */}
      <div className="sv-body">

        {/* Left sidebar */}
        <aside className="sv-sidebar">
          <p className="sv-sidebar-heading">MODERNIZATION JOURNEY</p>
          <p className="sv-sidebar-sub">Follow the guided stages below</p>

          {/* Upload — pre-phase step */}
          <div className="sv-upload-step">
            <button
              className={`sv-upload-btn ${activeStep === 'upload' ? 'active' : ''} sv-upload-status-${stepStatuses.upload}`}
              onClick={() => setActiveStep('upload')}
            >
              <UploadSidebarIcon status={stepStatuses.upload} />
              <span>Source Upload</span>
            </button>
          </div>

          <div className="sv-phase-divider" />

          {PHASES.map(phase => (
            <div key={phase.id} className="sv-phase-group">
              <button
                className={`sv-phase-header ${expandedPhases[phase.id] ? 'expanded' : ''}`}
                onClick={() => togglePhase(phase.id)}
              >
                <span className="sv-phase-label">{phase.label}</span>
                {expandedPhases[phase.id]
                  ? <ChevronUp size={13} />
                  : <ChevronDown size={13} />}
              </button>

              {expandedPhases[phase.id] && (
                <ul className="sv-step-list">
                  {phase.steps.map(step => {
                    const st = stepStatuses[step.id]
                    const isLocked = st === 'locked'
                    return (
                      <li key={step.id}>
                        <button
                          className={`sv-step-btn ${activeStep === step.id ? 'active' : ''} status-${st}`}
                          onClick={() => !isLocked && setActiveStep(step.id)}
                          disabled={isLocked}
                          title={isLocked ? (STAGE_INFO[step.id]?.prerequisite || 'Complete prior stages first') : undefined}
                        >
                          <StepDot status={st} />
                          <span>{step.label}</span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          ))}
        </aside>

        {/* Main content */}
        <main className="sv-main">
          <div className="sv-stage-header">
            <div className="sv-stage-title-row">
              <h2 className="sv-stage-title">{info.title}</h2>
              <span className={`sv-status-badge status-${currentStatus}`}>
                {currentStatus === 'in-progress' ? 'In Progress'
                  : currentStatus === 'complete'   ? 'Complete'
                  : currentStatus === 'error'      ? 'Error'
                  : currentStatus === 'locked'     ? 'Locked'
                  : 'Pending'}
              </span>
            </div>
            <p className="sv-stage-desc">{info.description}</p>
          </div>

          {renderContent()}
        </main>

        {/* Right sidebar — progress tracker */}
        <aside className="sv-progress-panel">
          <p className="sv-progress-heading">Modernization Progress</p>
          <p className="sv-progress-sub">Track your journey across all modernization phases</p>

          {phaseProgress.map((phase) => (
            <div key={phase.id} className="sv-progress-phase">
              <div className="sv-progress-phase-header">
                <div className={`sv-progress-icon ${phase.done === phase.total ? 'complete' : phase.active ? 'active' : ''}`}>
                  {phase.done === phase.total
                    ? <CheckCircle2 size={18} />
                    : phase.active
                    ? <Loader2 size={18} className="spin" />
                    : <Circle size={18} />}
                </div>
                <div className="sv-progress-phase-info">
                  <span className="sv-progress-phase-name">{phase.label}</span>
                  <span className="sv-progress-phase-sub">
                    {phase.done === phase.total ? 'Complete' : phase.active ? 'In Progress' : 'Upcoming'}
                  </span>
                </div>
                <span className="sv-progress-fraction">{phase.done}/{phase.total}</span>
              </div>
              <div className="sv-progress-bar-track">
                <div
                  className={`sv-progress-bar-fill ${phase.active ? 'active' : ''}`}
                  style={{ width: `${(phase.done / phase.total) * 100}%` }}
                />
              </div>
            </div>
          ))}

          {/* Reports — enabled once any stage is complete */}
          {(() => {
            const hasData = Object.values(stepStatuses).some(s => s === 'complete')
            return (
              <button
                className={`sv-reports-btn${hasData ? '' : ' sv-reports-btn-unavailable'}`}
                disabled={!hasData}
                onClick={() => hasData && onOpenReports?.()}
                title={hasData ? 'View reports for this run' : 'Reports available after a stage completes'}
              >
                <BarChart3 size={14} />
                Reports
              </button>
            )
          })()}

          {/* Need Help? — opens same Help panel as Workflow */}
          <button className="sv-help-btn" onClick={onOpenHelp}>
            <span>?</span> Need Help?
          </button>
        </aside>

      </div>
    </div>
  )
}

export default StandardView
